// Subscription Calculations Utility
// Core calculation functions for subscription system with top-ups and upgrades

/**
 * Calculate remaining value from current subscription
 * @param {Object} subscription - Current subscription data
 * @returns {Object} - Remaining operations, days, and monetary value
 */
export const calculateRemainingValue = (subscription) => {
  try {
    if (!subscription || !subscription.basePlan) {
      return {
        operations: 0,
        days: 0,
        monetaryValue: 0,
        percentage: 0
      };
    }

    const { basePlan, currentMonthUsage } = subscription;
    const now = new Date();
    const startDate = new Date(basePlan.startDate);
    const nextResetDate = new Date(basePlan.nextResetDate);
    
    // Calculate remaining operations
    const totalOperations = basePlan.limits?.firebase_operations || 0;
    const usedOperations = currentMonthUsage?.operations || 0;
    const remainingOperations = Math.max(0, totalOperations - usedOperations);
    
    // Calculate remaining days
    const totalDays = basePlan.validityDays || 30;
    const daysUsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, totalDays - daysUsed);
    
    // Calculate monetary value (prorated)
    const dailyRate = basePlan.price / totalDays;
    const monetaryValue = remainingDays * dailyRate;
    
    // Calculate percentage
    const percentage = totalDays > 0 ? (remainingDays / totalDays) * 100 : 0;
    
    return {
      operations: remainingOperations,
      days: remainingDays,
      monetaryValue: Math.round(monetaryValue * 100) / 100,
      percentage: Math.round(percentage * 100) / 100
    };
  } catch (error) {
    console.error('Error calculating remaining value:', error);
    return {
      operations: 0,
      days: 0,
      monetaryValue: 0,
      percentage: 0
    };
  }
};

/**
 * Calculate total entitlements from base plan and top-ups
 * @param {Object} basePlan - Base plan data
 * @param {Array} topups - Array of active top-ups
 * @returns {Object} - Total available limits
 */
export const calculateTotalEntitlements = (basePlan, topups = []) => {
  try {
    const baseLimits = basePlan?.limits || {
      firebase_operations: 0,
      storage_mb: 0,
      sales_documents: 0,
      parties: 0
    };
    
    const topupLimits = topups.reduce((total, topup) => {
      if (topup.status === 'active') {
        const rem = topup.remaining || topup.limits || {};
        return {
          firebase_operations: total.firebase_operations + Number(rem.firebase_operations || 0),
          storage_mb: total.storage_mb + Number(rem.storage_mb || 0),
          sales_documents: total.sales_documents + Number(rem.sales_documents || 0),
          parties: total.parties + Number(rem.parties || 0)
        };
      }
      return total;
    }, {
      firebase_operations: 0,
      storage_mb: 0,
      sales_documents: 0,
      parties: 0
    });
    
    return {
      firebase_operations: baseLimits.firebase_operations + topupLimits.firebase_operations,
      storage_mb: baseLimits.storage_mb + topupLimits.storage_mb,
      sales_documents: baseLimits.sales_documents + topupLimits.sales_documents,
      parties: baseLimits.parties + topupLimits.parties
    };
  } catch (error) {
    console.error('Error calculating total entitlements:', error);
    return basePlan?.limits || {
      firebase_operations: 0,
      storage_mb: 0,
      sales_documents: 0,
      parties: 0
    };
  }
};

/**
 * Calculate usage percentage
 * @param {number} used - Amount used
 * @param {number} total - Total available
 * @returns {number} - Usage percentage (0-100)
 */
export const calculateUsagePercentage = (used, total) => {
  if (!total || total === 0) return 0;
  return Math.min(100, Math.round((used / total) * 100 * 100) / 100);
};

/**
 * Get progress bar color based on usage percentage
 * @param {number} percentage - Usage percentage
 * @returns {string} - CSS color class
 */
export const getProgressColor = (percentage) => {
  if (percentage >= 95) return 'bg-red-500';
  if (percentage >= 80) return 'bg-yellow-500';
  if (percentage >= 60) return 'bg-blue-500';
  return 'bg-green-500';
};

/**
 * Calculate prorated charge for plan upgrade
 * @param {number} planPrice - Full plan price
 * @param {number} daysRemaining - Days remaining in current plan
 * @param {number} totalDays - Total days in current plan
 * @returns {number} - Prorated charge amount
 */
export const calculateProratedCharge = (planPrice, daysRemaining, totalDays) => {
  if (!totalDays || totalDays === 0) return planPrice;
  const dailyRate = planPrice / totalDays;
  return Math.round(daysRemaining * dailyRate * 100) / 100;
};

/**
 * Calculate next reset date
 * @param {Date} startDate - Plan start date
 * @param {number} bonusDays - Bonus days to add
 * @returns {Date} - Next reset date
 */
export const calculateNextResetDate = (startDate, bonusDays = 0) => {
  try {
    const date = new Date(startDate);
    const validityDays = 30; // Standard monthly plan
    date.setDate(date.getDate() + validityDays + bonusDays);
    return date;
  } catch (error) {
    console.error('Error calculating next reset date:', error);
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  }
};

/**
 * Prioritize top-up usage (FIFO - First In, First Out)
 * @param {Array} topups - Array of top-ups
 * @returns {Array} - Sorted top-ups by purchase date
 */
export const prioritizeTopupUsage = (topups = []) => {
  return topups
    .filter(topup => topup.status === 'active' && topup.remaining > 0)
    .sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
};

/**
 * Calculate upgrade benefits for Variant B
 * @param {Object} currentSubscription - Current subscription data
 * @param {Object} newPlan - New plan data
 * @returns {Object} - Upgrade benefits calculation
 */
export const calculateUpgradeBenefits = (currentSubscription, newPlan) => {
  try {
    const remaining = calculateRemainingValue(currentSubscription);
    const currentPlan = currentSubscription.basePlan;
    
    // Calculate operations bonus (remaining operations from old plan)
    const operationsBonus = remaining.operations;
    
    // Calculate time bonus (remaining days from old plan)
    const timeBonus = remaining.days;
    
    // Calculate new validity date
    const newValidityDate = new Date();
    newValidityDate.setDate(newValidityDate.getDate() + 30 + timeBonus);
    
    // Calculate total charge (full new plan price)
    const totalCharge = newPlan.price;
    
    // Calculate savings (monetary value of remaining old plan)
    const savings = remaining.monetaryValue;
    
    return {
      operationsBonus,
      timeBonus,
      newValidityDate,
      totalCharge,
      savings,
      netCost: totalCharge - savings,
      benefits: {
        operations: operationsBonus,
        timeExtension: timeBonus,
        newPlanLimits: newPlan.limits
      }
    };
  } catch (error) {
    console.error('Error calculating upgrade benefits:', error);
    return {
      operationsBonus: 0,
      timeBonus: 0,
      newValidityDate: new Date(),
      totalCharge: newPlan?.price || 0,
      savings: 0,
      netCost: newPlan?.price || 0,
      benefits: {
        operations: 0,
        timeExtension: 0,
        newPlanLimits: newPlan?.limits || {}
      }
    };
  }
};

/**
 * Calculate top-up purchase value
 * @param {Object} topup - Top-up package data
 * @param {Object} company - Company data
 * @returns {Object} - Purchase calculation
 */
export const calculateTopupPurchase = (topup, company) => {
  try {
    const price = topup.price;
    const limits = topup.limits;
    const value = {
      operations: limits.firebase_operations || 0,
      storage: limits.storage_mb || 0,
      documents: limits.sales_documents || 0,
      parties: limits.parties || 0
    };
    
    // Calculate cost per operation (for value analysis)
    const costPerOperation = value.operations > 0 ? price / value.operations : 0;
    
    return {
      price,
      value,
      costPerOperation: Math.round(costPerOperation * 10000) / 10000, // 4 decimal places
      savings: 0, // Could be calculated against individual operation costs
      totalValue: value.operations + value.storage + value.documents + value.parties
    };
  } catch (error) {
    console.error('Error calculating top-up purchase:', error);
    return {
      price: topup?.price || 0,
      value: {},
      costPerOperation: 0,
      savings: 0,
      totalValue: 0
    };
  }
};

/**
 * Calculate monthly reset data
 * @param {Object} subscription - Current subscription data
 * @returns {Object} - Reset calculation
 */
export const calculateMonthlyReset = (subscription) => {
  try {
    const { basePlan, currentMonthUsage, topups } = subscription;
    const now = new Date();
    
    // Archive current month usage
    const archivedUsage = {
      ...currentMonthUsage,
      archivedAt: now,
      month: now.toISOString().slice(0, 7) // YYYY-MM format
    };
    
    // Reset current month usage
    const resetUsage = {
      operations: 0,
      storage: 0,
      documents: 0,
      users: 0
    };
    
    // Calculate new reset date
    const newResetDate = calculateNextResetDate(now);
    
    // Keep top-ups intact (don't reset them)
    const preservedTopups = topups || [];
    
    return {
      archivedUsage,
      resetUsage,
      newResetDate,
      preservedTopups,
      resetAt: now
    };
  } catch (error) {
    console.error('Error calculating monthly reset:', error);
    return {
      archivedUsage: {},
      resetUsage: { operations: 0, storage: 0, documents: 0, users: 0 },
      newResetDate: new Date(),
      preservedTopups: [],
      resetAt: new Date()
    };
  }
};

/**
 * Calculate usage deduction with priority
 * @param {Object} subscription - Current subscription data
 * @param {string} resourceType - Type of resource to deduct
 * @param {number} amount - Amount to deduct
 * @returns {Object} - Deduction result
 */
export const calculateUsageDeduction = (subscription, resourceType, amount) => {
  try {
    const { basePlan, currentMonthUsage, topups } = subscription;
    const availableBase = (basePlan.limits?.[resourceType] || 0) - (currentMonthUsage?.[resourceType] || 0);
    
    let remainingToDeduct = amount;
    let deductionBreakdown = {
      fromBase: 0,
      fromTopups: []
    };
    
    // First, deduct from base plan
    if (remainingToDeduct > 0 && availableBase > 0) {
      const deductFromBase = Math.min(remainingToDeduct, availableBase);
      deductionBreakdown.fromBase = deductFromBase;
      remainingToDeduct -= deductFromBase;
    }
    
    // Then, deduct from top-ups (FIFO)
    if (remainingToDeduct > 0) {
      const prioritizedTopups = prioritizeTopupUsage(topups);
      
      for (const topup of prioritizedTopups) {
        if (remainingToDeduct <= 0) break;
        
        const availableTopup = topup.remaining?.[resourceType] || 0;
        if (availableTopup > 0) {
          const deductFromTopup = Math.min(remainingToDeduct, availableTopup);
          deductionBreakdown.fromTopups.push({
            topupId: topup.topupId,
            amount: deductFromTopup
          });
          remainingToDeduct -= deductFromTopup;
        }
      }
    }
    
    const success = remainingToDeduct === 0;
    const blockedAmount = remainingToDeduct;
    
    return {
      success,
      blockedAmount,
      deductionBreakdown,
      totalDeducted: amount - blockedAmount
    };
  } catch (error) {
    console.error('Error calculating usage deduction:', error);
    return {
      success: false,
      blockedAmount: amount,
      deductionBreakdown: { fromBase: 0, fromTopups: [] },
      totalDeducted: 0
    };
  }
};

/**
 * Calculate billing cycle information
 * @param {Object} subscription - Current subscription data
 * @returns {Object} - Billing cycle info
 */
export const calculateBillingCycle = (subscription) => {
  try {
    const { basePlan } = subscription;
    const now = new Date();
    const startDate = new Date(basePlan.startDate);
    const nextBillingDate = new Date(basePlan.nextBillingDate || basePlan.nextResetDate);
    
    const daysSinceStart = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    const daysUntilBilling = Math.floor((nextBillingDate - now) / (1000 * 60 * 60 * 24));
    
    const billingCycle = basePlan.billing || 'monthly';
    const cycleDays = billingCycle === 'monthly' ? 30 : 365;
    
    return {
      cycleDays,
      daysSinceStart,
      daysUntilBilling,
      nextBillingDate,
      billingCycle,
      isActive: daysUntilBilling > 0
    };
  } catch (error) {
    console.error('Error calculating billing cycle:', error);
    return {
      cycleDays: 30,
      daysSinceStart: 0,
      daysUntilBilling: 0,
      nextBillingDate: new Date(),
      billingCycle: 'monthly',
      isActive: false
    };
  }
};

/**
 * Calculate subscription health score
 * @param {Object} subscription - Current subscription data
 * @returns {Object} - Health score and recommendations
 */
export const calculateSubscriptionHealth = (subscription) => {
  try {
    const { basePlan, currentMonthUsage, topups } = subscription;
    const now = new Date();
    const nextResetDate = new Date(basePlan.nextResetDate);
    
    let healthScore = 100;
    const issues = [];
    const recommendations = [];
    
    // Check validity
    if (now > nextResetDate) {
      healthScore -= 50;
      issues.push('Subscription expired');
      recommendations.push('Renew subscription immediately');
    }
    
    // Check usage levels
    const usagePercentage = calculateUsagePercentage(
      currentMonthUsage?.operations || 0,
      basePlan.limits?.firebase_operations || 0
    );
    
    if (usagePercentage >= 95) {
      healthScore -= 30;
      issues.push('Usage at critical level (95%+)');
      recommendations.push('Purchase top-up or upgrade plan');
    } else if (usagePercentage >= 80) {
      healthScore -= 15;
      issues.push('Usage at high level (80%+)');
      recommendations.push('Consider purchasing top-up');
    }
    
    // Check top-up status
    const activeTopups = topups?.filter(t => t.status === 'active') || [];
    if (activeTopups.length === 0 && usagePercentage > 60) {
      healthScore -= 10;
      recommendations.push('Consider purchasing top-ups for flexibility');
    }
    
    // Check days remaining
    const daysRemaining = Math.floor((nextResetDate - now) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 3) {
      healthScore -= 20;
      issues.push('Subscription expires in 3 days or less');
      recommendations.push('Renew subscription to avoid service interruption');
    } else if (daysRemaining <= 7) {
      healthScore -= 10;
      recommendations.push('Consider renewing subscription soon');
    }
    
    const healthStatus = healthScore >= 80 ? 'excellent' : 
                        healthScore >= 60 ? 'good' : 
                        healthScore >= 40 ? 'fair' : 'poor';
    
    return {
      healthScore: Math.max(0, healthScore),
      healthStatus,
      issues,
      recommendations,
      usagePercentage,
      daysRemaining
    };
  } catch (error) {
    console.error('Error calculating subscription health:', error);
    return {
      healthScore: 0,
      healthStatus: 'poor',
      issues: ['Unable to calculate health'],
      recommendations: ['Contact support'],
      usagePercentage: 0,
      daysRemaining: 0
    };
  }
};

/**
 * Format date for display
 * @param {Date|string|number} date - Date to format
 * @param {string} format - Format type ('short', 'long', 'datetime')
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, format = 'short') => {
  try {
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    switch (format) {
      case 'long':
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      case 'datetime':
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      case 'time':
        return dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      case 'short':
      default:
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @param {string} locale - Locale code (default: 'en-IN')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'INR', locale = 'en-IN') => {
  try {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '₹0';
    }
    
    // Map currency symbols to ISO codes
    const currencyMap = {
      '₹': 'INR',
      '$': 'USD',
      '€': 'EUR',
      '£': 'GBP',
      '¥': 'JPY'
    };
    
    // Convert currency symbol to ISO code if needed
    const isoCurrency = currencyMap[currency] || currency;
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: isoCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    // Fallback: use the original currency symbol with amount
    const symbol = currency === 'INR' ? '₹' : currency;
    return `${symbol}${amount || 0}`;
  }
};